import { merge } from '../../helper';

import elementDisabled from '../../general/elementDisabled';
import isEmptyArray from '../../general/isEmpty/isEmptyArray';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';

import * as exList from '../../components/list';
import * as exElement from '../element';
import * as exIcon from '../icon';

import * as exMenu from './menu';

// ----------------------------------------------------------------------------

function create(config: Record<string, any> = {}): HTMLElement {
    // If selector is string
    if (typeof config.selector === 'string') {
        config.selector = document.querySelector(config.selector);
    }

    // Default Config
    const defConfig: Record<string, any> = {
        selector: document.body,
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const elements: Record<string, any> = {};

    // Create element (context menu)
    const elContextMenu = exElement.create({
        merge: defConfig,
        tag: 'ul',
        class: {
            new: 'context-menu',
        },
        style: {
            display: 'none',
        },
    });
    elements.element = elContextMenu;

    // ------------------------------------------------------------------------

    // Settings
    settings(elements, defConfig);

    // Events
    events(elements, defConfig);

    // ------------------------------------------------------------------------

    // Return
    return elements.element;
}

function settings(elements: Record<string, any>, config: Record<string, any> = {}): void {
    // If using data
    if (!isEmptyArray(config.data)) {
        // Create element (context menu items)
        const contextMenuItems = data(elements.element, {
            merge: exElement.clear({ ...config }),
        });
        elements.items = contextMenuItems;
    }
}

function events(elements: Record<string, any>, config: Record<string, any> = {}): void {
    // Create event (context menu)
    config.selector.addEventListener('contextmenu', function (evt): any {
        evt.preventDefault();

        // If there is element (context menu)
        if (evt.target.closest('.context-menu')) {
            return;
        }

        // Position Menu
        positionMenu(elements.element, evt);

        // Show Context Menu
        elements.element.style.display = '';
    });

    // Create event (click)
    document.body.addEventListener('click', function (evt): any {
        const target = evt.target as HTMLElement;

        if (target.offsetParent !== elements.element) {
            elements.element.style.display = 'none';
        }
    });
}

// ----------------------------------------------------------------------------

function data(element: HTMLElement, config: Record<string, any> = {}): any[] {
    // Default Config
    const defConfig: Record<string, any> = {};

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const list: any[] = [];

    // Foreach Data
    for (const itemA of defConfig.data) {
        // If itemA is object
        if (!isEmptyObject(itemA)) {
            // Create element (context menu item)
            const contextMenuItem = add({
                merge: {
                    /*  ...defConfig, */
                    ...itemA,
                    data: itemA.data,
                },
            });
            element.appendChild(contextMenuItem.element);

            // List Push
            list.push(contextMenuItem.element);
        }
        // If itemA is array
        else if (!isEmptyArray(itemA)) {
            // Foreach ItemA
            for (const itemB of itemA) {
                // Create element (context menu item)
                const contextMenuItem = add({
                    merge: defConfig,
                    html: itemB,
                });
                element.appendChild(contextMenuItem.element);

                // List Push
                list.push(contextMenuItem.element);
            }
        }
        // If itemA is string
        else if (typeof itemA === 'string') {
            // If using divider
            if (itemA === 'divider') {
                // Create element (divider)
                const elDivider = exList.divider({
                    merge: defConfig.divider,
                });
                element.appendChild(elDivider);

                // List Push
                list.push(elDivider);
            }
        }
    }

    // ------------------------------------------------------------------------

    // Return
    return list;
}

function add(config: Record<string, any> = {}): Record<string, any> {
    // Default Config
    const defConfig: Record<string, any> = {
        hover: true,
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const elements: Record<string, any> = {};

    // Create element (item)
    const elItem = exElement.create({
        merge: defConfig,
        tag: 'li',
        class: {
            new: 'context-menu-item',
        },
    });
    elements.element = elItem;

    // ------------------------------------------------------------------------

    // Create element (item content)
    const elItemContent = exElement.create({
        merge: defConfig.content,
        class: {
            new: 'context-menu-item-content',
        },
    });
    elItem.appendChild(elItemContent);
    elements.itemContent = elItem;

    // Create element (item text)
    const elItemText = exElement.create({
        merge: defConfig.contentText,
        class: {
            new: 'context-menu-item-text',
        },
        html: defConfig.title,
    });
    elItemContent.appendChild(elItemText);
    elements.itemText = elItem;

    // If using icon
    if (defConfig.icon) {
        // Create element (item icon)
        const elItemIcon = exIcon.create({
            merge: defConfig.icon,
        });
        elItemContent.prependChild(elItemIcon);
        elements.itemIcon = elItem;
    }

    // ------------------------------------------------------------------------

    // If using data
    if (!isEmptyArray(defConfig.data)) {
        // Create element (icon)
        const elIcon = exIcon.create({
            class: {
                new: 'fa-solid fa-angle-right',
            },
        });
        elItem.appendChild(elIcon);

        // --------------------------------------------------------------------

        // Create element (menu)
        const menuItem = exMenu.create({
            merge: exElement.clear({ ...defConfig }),
        });
        elItem.appendChild(menuItem.element);
        elements.menu = menuItem;

        // --------------------------------------------------------------------

        // If using hover
        if (defConfig.hover === true) {
            // Create event (mouse enter)
            elItem.addEventListener('mouseenter', function (): any {
                const elContextMenu = this.closest('.context-menu');
                if (!elContextMenu) {
                    return;
                }

                // If there is disabled
                if (elItem.hasAttribute('disabled')) {
                    return;
                }

                //
                menuItem.element.style.display = '';

                //
                menuItem.element.style.left = elContextMenu.clientWidth + 'px';
                menuItem.element.style.top = -1 + 'px';
            });

            // Create event (mouse leave)
            elItem.addEventListener('mouseleave', function (): any {
                menuItem.element.style.display = 'none';
            });
        }
    }

    // If using function disabled
    if (typeof defConfig.fnDisabled === 'function') {
        // Create event (context menu)
        defConfig.selector.addEventListener('contextmenu', function (): any {
            // Function Disabled
            const res = defConfig.fnDisabled();

            //
            elementDisabled(elItem, res);
        });
    }

    // ------------------------------------------------------------------------

    // Return
    return elements;
}

// ----------------------------------------------------------------------------

function positionMenu(element: HTMLElement, evt: Record<string, any>): void {
    const pos = getPosition(evt);

    // Get window dimension
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Get menu dimension
    const menuWidth = element.offsetWidth + 4;
    const menuHeight = element.offsetHeight + 4;

    //
    if (windowWidth - pos.x < menuWidth) {
        element.style.left = windowWidth - menuWidth + 'px';
    } else {
        element.style.left = pos.x + 4 + 'px';
    }

    //
    if (windowHeight - pos.y < menuHeight) {
        element.style.top = windowHeight - menuHeight + 'px';
    } else {
        element.style.top = pos.y + 4 + 'px';
    }
}

// ----------------------------------------------------------------------------

function getPosition(evt: any): Record<string, any> {
    let posX = 0;
    let posY = 0;

    if (!evt) {
        evt = window.event;
    }

    //
    if (evt.pageX || evt.pageY) {
        posX = evt.pageX;
        posY = evt.pageY;
    }
    //
    else if (evt.clientX || evt.clientY) {
        posX = evt.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
        posY = evt.clientY + document.body.scrollTop + document.documentElement.scrollTop;
    }

    //
    return {
        x: posX,
        y: posY,
    };
}

// ----------------------------------------------------------------------------

export { create, data, add };
